import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, promoAPI, referralAPI, ReferralSummary } from '../services/api';
import './ProfilePage.css';

interface UserProfile {
  name: string;
  email: string;
  balance: number;
  lastLogin: string;
  reportsCount: number;
}

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation('profile');
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoLoading, setPromoLoading] = useState<boolean>(false);
  const [promoResult, setPromoResult] = useState<string>('');
  const [promoError, setPromoError] = useState<string>('');
  const [referral, setReferral] = useState<ReferralSummary | null>(null);
  const [refCopied, setRefCopied] = useState<boolean>(false);
  
  // Проверка авторизации
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [navigate, isAuthenticated, authLoading]);
  
  // Загрузка данных пользователя из authUser
  // reports_count приходит вместе с /users/me — отдельный fetch /reports не нужен
  useEffect(() => {
    if (!authUser) return;

    const lastLogin = authUser.last_login
      ? new Date(authUser.last_login).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU')
      : t('no_data');

    const profile: UserProfile = {
      name: authUser.name || '',
      email: authUser.email || '',
      balance: authUser.balance || 0,
      lastLogin: lastLogin,
      reportsCount: authUser.reports_count ?? 0,
    };

    setUserProfile(profile);
    setName(profile.name);
    setIsLoading(false);
  }, [authUser, i18n.language, t]);

  // Загружаем сводку реферальной программы (best-effort)
  useEffect(() => {
    if (!authUser) return;
    referralAPI.getSummary().then(setReferral).catch(() => {
      /* silent — affiliate card просто не отрисуется */
    });
  }, [authUser]);

  const handleCopyReferral = async () => {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };
  
  const handleSaveProfile = async () => {
    if (!userProfile || !authUser) return;
    
    try {
      // Обновляем профиль пользователя через API
      await userAPI.updateProfile({ name });
      
      // Обновляем локальное состояние
      setUserProfile({
        ...userProfile,
        name
      });
      
      // Обновляем данные пользователя в контексте
      refreshUser();
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };
  
  const handleTopUp = () => {
    if (!userProfile) return;
    navigate('/payment/top-up');
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoResult('');
    setPromoError('');
    try {
      const response = await promoAPI.redeem(promoCode.trim());
      setPromoResult(response.data.message);
      setPromoCode('');
      setUserProfile(prev => prev ? { ...prev, balance: response.data.new_balance } : prev);
      refreshUser();
    } catch (error: any) {
      const detail = error.response?.data?.detail || t('promo.error_prefix');
      setPromoError(detail);
    } finally {
      setPromoLoading(false);
    }
  };
  
  if (isLoading || authLoading) {
    return <div className="loading">{t('loading')}</div>;
  }
  
  if (!userProfile) {
    return <div className="error">{t('save_error')}</div>;
  }
  
  return (
    <div className="profile-page">
      <h1>{t('title')}</h1>
      
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            {userProfile.name.charAt(0).toUpperCase()}
          </div>
          <div className="profile-info">
            {isEditing ? (
              <div className="edit-name-form">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('name_placeholder')}
                />
                <div className="edit-buttons">
                  <button onClick={handleSaveProfile} className="save-button">{t('save_button')}</button>
                  <button onClick={() => setIsEditing(false)} className="cancel-button">{t('cancel_button')}</button>
                </div>
              </div>
            ) : (
              <>
                <h2>{userProfile.name}</h2>
                <button onClick={() => setIsEditing(true)} className="edit-button">
                  {t('edit_button')}
                </button>
              </>
            )}
            <p className="email">{userProfile.email}</p>
          </div>
        </div>
        
        <div className="profile-details">
          <div className="detail-item">
            <span className="detail-label">{t('balance_label')}:</span>
            <span className="detail-value">{t('balance_value', { count: userProfile.balance })}</span>
            <button onClick={handleTopUp} className="top-up-button">{t('payment.title')}</button>
          </div>
          
          <div className="detail-item">
            <span className="detail-label">{t('last_login_label')}:</span>
            <span className="detail-value">{userProfile.lastLogin}</span>
          </div>
          
          <div className="detail-item">
            <span className="detail-label">{t('reports_count_label')}:</span>
            <span className="detail-value">{userProfile.reportsCount}</span>
          </div>
        </div>
      </div>

      {referral && (
        <div className="affiliate-card-mini">
          <div className="affiliate-card-mini-header">
            <h3>{t('affiliate.card.title', 'Партнёрская программа')}</h3>
            <button onClick={() => navigate('/affiliate')} className="affiliate-card-mini-link">
              {t('affiliate.card.more', 'Подробнее')} →
            </button>
          </div>
          <div className="affiliate-card-mini-link-row">
            <code>{referral.share_url}</code>
            <button onClick={handleCopyReferral}>
              {refCopied ? t('affiliate.copied', 'Скопировано!') : t('affiliate.copy', 'Копировать')}
            </button>
          </div>
          <div className="affiliate-card-mini-stats">
            <div>
              <span className="label">{t('affiliate.stats.invited', 'Приглашено')}</span>
              <span className="value">{referral.invited_count}</span>
            </div>
            <div>
              <span className="label">{t('affiliate.stats.earned_ticks', 'Заработано тиков')}</span>
              <span className="value">{referral.earned_ticks_total}</span>
            </div>
            <div>
              <span className="label">{t('affiliate.stats.balance_rub', 'Баланс ₽')}</span>
              <span className="value">{parseFloat(referral.bonus_balance_rub).toFixed(2)}</span>
            </div>
            <div>
              <span className="label">{t('affiliate.stats.on_hold', 'В удержании ₽')}</span>
              <span className="value">{parseFloat(referral.pending_rub).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="promo-section">
        <h3>{t('promo.title')}</h3>
        <div className="promo-input-group">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder={t('promo.placeholder')}
            maxLength={50}
            onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
          />
          <button
            onClick={handleRedeemPromo}
            disabled={promoLoading || !promoCode.trim()}
            className="promo-button"
          >
            {promoLoading ? t('promo.loading') : t('promo.submit')}
          </button>
        </div>
        {promoResult && <div className="promo-message success">{promoResult}</div>}
        {promoError && <div className="promo-message error">{promoError}</div>}
      </div>
    </div>
  );
};

export default ProfilePage;
